import React, { useState, useEffect, useRef } from 'react';
import { Loader2, ArrowLeft, RefreshCw, AlertCircle } from 'lucide-react';
import { supabaseHelpers } from '../lib/supabase';
import { normalizeImageUrl } from '../utils/api';

// Renders an in-progress presentation generation. The frontend lost the
// SSE stream (refresh, deploy, navigation) so we fall back to polling
// the projects row every few seconds and rendering whatever slides are
// already persisted to analysis_data.slides[]. Per-slide DB persistence
// happens in app.py's job loop (Layer B), so this view reflects the
// real state of the job even if the worker has restarted.
//
// Props:
//   projectId       — the in-progress project to watch
//   onComplete(p)   — called once when project.status flips to 'completed';
//                     parent re-routes to the regular workspace.
//   onBack          — back to dashboard
//   onRetry         — called if user clicks Regenerate after a failure

export function GenerationProgressView({ projectId, onComplete, onBack, onRetry }) {
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const completedFiredRef = useRef(false);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const { data, error: fetchErr } = await supabaseHelpers.getProjectFull(projectId);
        if (cancelled) return;
        if (fetchErr) {
          setError(fetchErr.message || 'Failed to load project');
          return;
        }
        if (data) {
          setProject(data);
          setError(null);
          // Status transitions
          if (data.status === 'completed' && !completedFiredRef.current) {
            completedFiredRef.current = true;
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            // Small delay so the user sees "100%" before the route change
            setTimeout(() => onComplete?.(data), 600);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Network error');
      }
    };

    fetchOnce();
    // Poll every 3 seconds while in progress. Stops once status flips.
    pollIntervalRef.current = setInterval(fetchOnce, 3000);

    return () => {
      cancelled = true;
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [projectId, onComplete]);

  const slides = project?.analysis_data?.slides || [];
  const completed = project?.completed_slides ?? slides.length;
  const total = project?.total_slides ?? Math.max(slides.length, 1);
  const pct = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  const status = project?.status || 'generating';
  const title = project?.name || 'Generating presentation';

  // Placeholder cards for slides not yet completed. We show the same grid
  // dimensions whether the slide is done or pending so the layout doesn't
  // shift as slides arrive.
  const pendingCount = Math.max(0, total - completed);
  const pendingPlaceholders = Array.from({ length: pendingCount }, (_, i) => completed + i);

  // Latest completed slide is the focal preview on the right — mirrors
  // the workspace layout where the most recent slide takes the main area.
  const latestSlide = slides.length > 0 ? slides[slides.length - 1] : null;
  const latestPreview = latestSlide ? normalizeImageUrl(latestSlide.preview_url) : '';

  return (
    <div className="h-dvh flex flex-col bg-background">
      <div className="flex-1 flex overflow-hidden">
        {/* ── Left: chat-style sidebar showing progress chips ── */}
        <div
          className="flex flex-col h-full border-r border-border/30 bg-background overflow-hidden"
          style={{ width: '380px', flexShrink: 0 }}
        >
          <div className="border-b border-border/30 px-4 py-3 flex items-center gap-2">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            <div className="rounded-2xl bg-violet-600 text-white px-4 py-2.5 text-sm font-medium inline-block max-w-full">
              {title}
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="inline-flex w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-500/15 items-center justify-center">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-600" />
              </span>
              <span>Resumed an in-progress generation</span>
            </div>

            {status === 'failed' ? (
              <div className="flex items-start gap-3 p-3 rounded-xl border border-red-500/20 bg-red-500/5">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground">Generation interrupted</p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    The job didn't finish. Completed slides are saved.
                  </p>
                  <button
                    onClick={onRetry}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 transition-colors"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Regenerate
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-border/40 bg-card/50 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Loader2 className="w-4 h-4 text-violet-600 animate-spin shrink-0" />
                  <p className="text-xs text-foreground font-medium">
                    Generating slide {Math.min(completed + 1, total)} of {total}
                  </p>
                  <span className="ml-auto text-[11px] font-mono text-muted-foreground">{pct}%</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                  You can leave this page — generation continues in the background.
                </p>
              </div>
            )}

            {error && (
              <div className="p-2.5 rounded-lg border border-amber-500/30 bg-amber-500/5 text-[11px] text-amber-700 dark:text-amber-300">
                Couldn't refresh: {error}. Still trying…
              </div>
            )}
          </div>
        </div>

        {/* ── Right: slide grid + latest preview ── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-8 py-6">
            {latestPreview && (
              <div className="mb-6 rounded-2xl border border-border bg-card overflow-hidden aspect-video relative shadow-sm">
                <img
                  src={latestPreview}
                  alt={`Slide ${slides.length}`}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-md bg-black/60 text-white text-xs font-medium">
                  Slide {slides.length} of {total}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {slides.map((slide, idx) => {
                const preview = normalizeImageUrl(slide.preview_url);
                return (
                  <div
                    key={idx}
                    className="rounded-xl border border-border bg-card overflow-hidden aspect-video relative group"
                  >
                    {preview ? (
                      <img
                        src={preview}
                        alt={`Slide ${idx + 1}`}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
                        Slide {idx + 1}
                      </div>
                    )}
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-black/60 text-white text-[10px] font-medium">
                      {idx + 1}
                    </div>
                  </div>
                );
              })}
              {pendingPlaceholders.map((idx) => (
                <div
                  key={`pending-${idx}`}
                  className="rounded-xl border border-dashed border-border bg-card/40 aspect-video relative flex items-center justify-center"
                >
                  <Loader2 className="w-5 h-5 text-muted-foreground animate-spin opacity-50" />
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-muted text-muted-foreground text-[10px] font-medium">
                    {idx + 1}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GenerationProgressView;
